import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../../supabaseClient';
import { Dialog, Transition } from '@headlessui/react';
import { format, parseISO, isValid } from 'date-fns';
import AdminButton from '../../../components/admin/AdminButton';
import toast from 'react-hot-toast';
import { trackShipments, cancelFedexShipment, downloadFedexLabel, getProofOfDelivery } from '../../../services/fedexApi';
import TrackingResult from './TrackingResult';
import { getStatusBadgeClass, getStatusSelectClass, getStatusLabel } from '../../../utils/status';
import AdminLoader from '../AdminLoader';
import { UserIcon, UserGroupIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { generateAndUploadShippingForm } from '../../../utils/generateReceipt';

const ShipmentsList = () => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shipmentModal, setShipmentModal] = useState({ open: false, loading: false, shipment: null, error: null });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [documentModal, setDocumentModal] = useState({ open: false, title: '', url: '' });
  const [isRefreshingForm, setIsRefreshingForm] = useState(false);
  const [searchParams, setSearchParams] = useState({ searchTerm: '', searchStatus: '', startDate: '', endDate: '', searchScope: 'all' });
  const [trackingModal, setTrackingModal] = useState({ open: false, loading: false, trackingNumber: null, trackingInfo: null, error: null });
  // État de chargement pour la Preuve de livraison (par numéro de suivi)
  const [podLoadingTN, setPodLoadingTN] = useState(null);
  // Modale de changement de statut
  const [statusModal, setStatusModal] = useState({ open: false, loading: false, shipment: null, newStatus: null, error: null });
  // Modale de confirmation d'annulation FedEx
  const [cancelModal, setCancelModal] = useState({ open: false, loading: false, shipment: null, error: null });
  // Statistiques expéditions
  const [stats, setStats] = useState({ loading: false, total: 0, fedex: 0, error: null });
  const [statsDates, setStatsDates] = useState({ start: '', end: '' });

  // Ancre et scroll fluide vers le haut
  const topRef = useRef(null);
  const scrollToTop = () => {
    if (topRef.current && typeof topRef.current.scrollIntoView === 'function') {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (typeof window !== 'undefined' && window.scrollTo) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDownloadFedexLabel = async (event, shipment) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      const blob = await downloadFedexLabel(shipment.label_url);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `etiquette-fedex-${shipment.tracking_number || 'expedition'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Impossible de télécharger l'étiquette FedEx";
      toast.error(message);
    }
  };

  /**
   * Affiche la Preuve de livraison (SPOD) via l'API backend, puis ouvre la modale document.
   * - Crée une URL Blob à partir des données base64 retournées
   * - Gère les formats PDF/PNG
   */
  const handleViewProofOfDelivery = async (shipment) => {
    const tn = shipment?.tracking_number;
    if (!tn || tn.startsWith('CD')) return;
    setPodLoadingTN(tn);
    try {
      const resp = await getProofOfDelivery(tn);
      const { documentFormat = 'PDF', data, filename } = resp || {};
      if (!data) throw new Error("Document SPOD indisponible");

      // Décoder le base64 en Blob
      const byteChars = atob(data);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
      const byteArray = new Uint8Array(byteNumbers);

      const fmt = (documentFormat || 'PDF').toUpperCase();
      const mime = fmt === 'PNG' ? 'image/png' : 'application/pdf';
      const blob = new Blob([byteArray], { type: mime });
      const url = URL.createObjectURL(blob);

      setDocumentModal({ open: true, title: `Preuve de livraison (${filename || tn})`, url });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Erreur lors de la récupération de la preuve de livraison.";
      toast.error(msg);
    } finally {
      setPodLoadingTN(null);
    }
  };

/**
 * Regénère le PDF du formulaire d'expédition avec les données fraîches depuis la BDD.
 * - Recharge l'expédition avec les relations `payment` et `user` (source de vérité)
 * - Construit l'objet userDetails attendu par `generateAndUploadShippingForm`
 * - Upload le nouveau PDF (upsert) et ouvre la modale document avec une URL signée bustée
 *
 * @param {number} shipmentId - Identifiant de l'expédition
 */
const handleRefreshShippingForm = async (shipmentId) => {
  if (!shipmentId || isRefreshingForm) return;
  setIsRefreshingForm(true);
  try {
    // 1) Récupérer l'expédition fraîche avec payment et user
    const { data, error } = await supabase
      .from('shipments')
      .select(`*, payment:payments(*, user:users(*))`)
      .eq('id', shipmentId)
      .single();
    if (error) throw error;

    const fresh = normalizeCancelled(data);

    // 2) Extraire les infos utiles
    const payment = fresh?.payment;
    const user = payment?.user;

    // documents_to_be_sent peut être JSON ou string JSON
    let docs = fresh?.documents_to_be_sent;
    if (typeof docs === 'string') {
      try { docs = JSON.parse(docs || '[]'); } catch { docs = []; }
    }
    const firstDoc = Array.isArray(docs) && docs.length > 0 ? docs[0] : null;
    const actTitle = firstDoc?.act_title || firstDoc?.actTitle || fresh?.act_title || 'Acte';

    // 3) Construire userDetails pour la génération
    // Parser family_members si nécessaire (peut être string JSON)
    let familyMembers = fresh?.family_members || [];
    if (typeof familyMembers === 'string') {
      try { familyMembers = JSON.parse(familyMembers || '[]'); } catch { familyMembers = []; }
    }

    const userDetails = {
      transaction_id: payment?.transaction_id,
      actTitle,
      userName: `${user?.firstname || ''} ${user?.lastname || ''}`.trim() || fresh?.recipient_name || 'Client',
      userLastName: user?.lastname || '',
      userFirstName: user?.firstname || '',
      userBirthDate: user?.birth_date || '',
      userEmail: user?.email || '',
      userPhone: user?.phone || '',
      userAddress: user?.address || fresh?.recipient_address || '',

      recipientName: fresh?.recipient_name || '',
      recipientAddress: fresh?.recipient_address || '',
      recipientPhone: fresh?.recipient_phone || '',

      withFamily: !!fresh?.with_family,
      familyMembers,
    };

    if (!userDetails.transaction_id) {
      throw new Error("transaction_id introuvable pour cette expédition (payment manquant)");
    }

    // 4) Générer et uploader le nouveau PDF
    await generateAndUploadShippingForm(userDetails);

    // 5) Mettre à jour la modale avec les données fraîches
    setShipmentModal((prev) => ({ ...prev, shipment: fresh }));

    // 6) Ouvrir le document actualisé avec une URL signée bustée (anti-cache)
    const filename = `Formulaire_${userDetails.transaction_id}.pdf`;
    try {
      const signedUrl = await getSignedUrl('formulaires', filename);
      setDocumentModal({ open: true, title: 'Formulaire d\'expédition', url: `${signedUrl}&t=${Date.now()}` });
    } catch (e) {
      // Si récupération de l'URL échoue, on notifie seulement
      console.warn('Impossible d\'ouvrir automatiquement le formulaire régénéré:', e);
    }

    toast.success('Formulaire d\'expédition régénéré avec succès.');
  } catch (err) {
    console.error('Erreur refresh formulaire:', err);
    toast.error(err?.message || "Échec de la régénération du formulaire d'expédition.");
  } finally {
    setIsRefreshingForm(false);
  }
};
  const goToPage = (newPage) => {
    setPage(newPage);
    scrollToTop();
  };

  // Normalise l'objet expédition pour exposer un booléen `cancelled`
  // - via status "cancelled"
  // - ou présence de `cancelled_at`
  // - ou relation `cancelled_shipments` non vide
  const normalizeCancelled = useCallback((s) => {
    const rel = Array.isArray(s.cancelled_shipments) ? s.cancelled_shipments : [];
    const cancelled = s.status === 'cancelled' || !!s.cancelled_at || rel.length > 0;
    return { ...s, cancelled };
  }, []);

  useEffect(() => {
    // Charge les expéditions avec leurs relations utiles et infos d'annulation
    const fetchShipments = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await supabase
          .from('shipments')
          .select(`*, payment:payments(*, user:users(*)), cancelled_shipments(*)`)
          .order('created_at', { ascending: false });
        if (error) throw error;
        const list = (data || []).map(normalizeCancelled);
        setShipments(list);
      } catch (err) {
        setError(err.message || 'Erreur lors du chargement des expéditions.');
      } finally {
        setLoading(false);
      }
    };
    fetchShipments();
  }, [normalizeCancelled]);

  // Helpers dates (YYYY-MM-DD) → gérer journée complète
  const toYMD = useCallback((date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, []);
  
  const nextDayYMD = useCallback((ymd) => {
    try {
      const [y, m, d] = ymd.split('-').map(Number);
      const dt = new Date(y, (m || 1) - 1, d || 1);
      dt.setDate(dt.getDate() + 1);
      return toYMD(dt);
    } catch {
      return ymd;
    }
  }, [toYMD]);

  // Helper de formatage de date robuste (accepte string ISO ou Date)
  const formatDate = (value) => {
    if (!value) return '—';
    try {
      let dt = value;
      if (typeof value === 'string') {
        // Essaye un parse ISO explicite pour éviter les incohérences
        const parsed = parseISO(value);
        dt = isValid(parsed) ? parsed : new Date(value);
      }
      if (!(dt instanceof Date) || !isValid(dt)) return '—';
      return format(dt, 'dd/MM/yyyy');
    } catch {
      return '—';
    }
  };

  const formatBirthDate = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
    return match ? `${match[3]}/${match[2]}/${match[1]}` : 'Non renseignée';
  };

  /**
   * Récupère les statistiques d'expéditions (compteurs) avec option de filtre par date.
   * - total: nombre d'enregistrements dans `shipments`
   * - fedex: nombre d'enregistrements dont `tracking_number` ne commence pas par 'CD'
   * @param {string} [start] - date ISO (YYYY-MM-DD) minimale incluse
   * @param {string} [end] - date ISO (YYYY-MM-DD) maximale incluse
   */
  const fetchStats = useCallback(async (start, end) => {
    try {
      setStats((s) => ({ ...s, loading: true, error: null }));
      // Base total
      let totalQuery = supabase
        .from('shipments')
        .select('id', { count: 'exact', head: true });
      if (start) totalQuery = totalQuery.gte('created_at', `${start} 00:00:00`);
      if (end) totalQuery = totalQuery.lt('created_at', `${nextDayYMD(end)} 00:00:00`);
      const { count: totalCount, error: totalError } = await totalQuery;
      if (totalError) throw totalError;

      // Base FedEx (tracking_number ne commence pas par 'CD')
      let fedexQuery = supabase
        .from('shipments')
        .select('id', { count: 'exact', head: true })
        .not('tracking_number', 'ilike', 'CD%');
      if (start) fedexQuery = fedexQuery.gte('created_at', `${start} 00:00:00`);
      if (end) fedexQuery = fedexQuery.lt('created_at', `${nextDayYMD(end)} 00:00:00`);
      const { count: fedexCount, error: fedexError } = await fedexQuery;
      if (fedexError) throw fedexError;

      setStats({ loading: false, total: totalCount || 0, fedex: fedexCount || 0, error: null });
    } catch (err) {
      setStats({ loading: false, total: 0, fedex: 0, error: err.message || 'Erreur de statistiques' });
    }
  }, [nextDayYMD]);

  // Charger les stats initiales (toutes dates)
  useEffect(() => {
    // Charger les stats au montage
    fetchStats();
  }, [fetchStats]);

  // Fonction de formatage de statut maintenant gérée par le composant AdminTable

  // Définition des colonnes pour le tableau
  const columns = [
    { label: 'Destinataire', key: 'recipient_name' },
    { label: 'Document', key: 'documents_to_be_sent' },
    { label: 'Transporteur', key: 'carrier' },
    { 
      label: 'Statut', 
      key: 'status', 
      type: 'status',
      labels: {
        delivered: 'Livré',
        pending: 'En attente',
        created: 'Expédition créée',
        returned: 'Retourné',
        in_progress: 'En cours'
      }
    },
    { label: 'Date', key: 'created_at', type: 'date' }
  ];

  // Scoring local pour trier les résultats par pertinence selon les tokens
  // Chaque token trouvé ajoute des points; un bonus est ajouté si tous les tokens sont présents
  // et un bonus supplémentaire pour une occurrence de la phrase complète
  /**
   * Supprime les accents/diacritiques d'une chaîne pour rendre la recherche insensible aux accents.
   * Exemple: "Kouamé Affoué" -> "Kouame Affoue"
   */
  const removeAccents = (value) => {
    if (typeof value !== 'string') return value;
    return value.normalize('NFD').replace(/\p{Diacritic}+/gu, '');
  };

  /**
   * Nettoie un texte en ne gardant que les lettres, espaces et apostrophes
   * Exemple: "Jean-Pierre (Paris)" -> "Jean Pierre Paris"
   */
  const cleanText = (value) => {
    if (typeof value !== 'string') return '';
    // Remplacer tout ce qui n'est pas une lettre, espace ou apostrophe par un espace
    return value.replace(/[^\p{L}\s']/gu, ' ').replace(/\s+/g, ' ').trim();
  };

  /**
   * Construit un texte de référence par expédition pour le scoring
   * Inclut les champs importants et les membres de la famille
   */
  const buildHaystack = (shipment) => {
    // Champs principaux
    const mainFields = [
      shipment.recipient_name,
      shipment.family_members,
      shipment.recipient_address,
      shipment.recipient_phone,
      shipment.tracking_number,
      shipment.carrier,
      shipment.status
    ].filter(Boolean).join(' ');
    
    // Informations utilisateur liées
    const userInfo = shipment.payment?.user ? [
      shipment.payment.user.firstname,
      shipment.payment.user.lastname,
      shipment.payment.user.email,
      shipment.payment.user.phone
    ].filter(Boolean).join(' ') : '';
    
    // Membres de la famille (avec gestion des différents formats possibles)
    let familyMembers = '';
    if (shipment.family_members) {
      let members = [];
      try {
        // Si c'est une chaîne JSON, la parser
        const familyData = typeof shipment.family_members === 'string' 
          ? JSON.parse(shipment.family_members) 
          : shipment.family_members;
        
        // Traiter selon le format
        if (Array.isArray(familyData)) {
          members = familyData.map(member => {
            if (typeof member === 'string') return member;
            if (member.name || member.fullname) return member.name || member.fullname;
            if (member.firstname || member.lastname) {
              return [member.firstname, member.lastname].filter(Boolean).join(' ');
            }
            return '';
          });
        } else if (typeof familyData === 'object') {
          // Si c'est un objet unique
          if (familyData.name || familyData.fullname) members.push(familyData.name || familyData.fullname);
          else if (familyData.firstname || familyData.lastname) {
            members.push([familyData.firstname, familyData.lastname].filter(Boolean).join(' '));
          }
        } else if (typeof familyData === 'string') {
          members.push(familyData);
        }
        
        familyMembers = members.filter(Boolean).join(' ');
      } catch (e) {
        console.error('Erreur lors du parsing des membres de famille:', e);
      }
    }
    
    // Combiner tous les champs
    return [mainFields, userInfo, familyMembers].filter(Boolean).join(' ').toLowerCase();
  };

  // Normaliser le haystack sans accents
  const buildHaystackNormalized = (shipment) => removeAccents(buildHaystack(shipment));

  /**
   * Calcule un score de pertinence pour l'expédition donnée.
   * - +100 par token présent (favorise les fiches contenant tous les mots)
   * - +5 par occurrence totale des tokens (désambiguisation fine)
   * - +10 si la phrase complète apparaît (bonus phrase exacte)
   */
  const computeSearchScore = (shipment, tokens, full) => {
    const text = buildHaystackNormalized(shipment);
    let matchedTokens = 0;
    let occurrences = 0;
    for (const t of tokens) {
      if (!t) continue;
      if (text.includes(t)) {
        matchedTokens++;
        // Compte des occurrences de chaque token
        let idx = text.indexOf(t);
        while (idx !== -1) {
          occurrences++;
          idx = text.indexOf(t, idx + t.length);
        }
      }
    }
    const phraseBonus = full && text.includes(full) ? 10 : 0;
    return matchedTokens * 100 + occurrences * 5 + phraseBonus;
  };

  // Gestion de l'édition d'une expédition
  const handleEdit = (shipment) => {
    window.location.href = `/admin/shipments/edit/${shipment.id}`;
  };
  
  // Handler pour ouvrir la modale et charger les infos d'expédition
  const handleShowShipment = async (shipmentId) => {
    // Ouvre la modale et charge l'expédition en incluant la relation cancelled_shipments
    // pour pouvoir dériver le booléen "cancelled" comme dans la liste principale.
    setShipmentModal({ open: true, loading: true, shipment: null, error: null });
    try {
      const { data, error } = await supabase
        .from('shipments')
        .select('*, payment:payments(*, user:users(*)), cancelled_shipments(*)')
        .eq('id', shipmentId)
        .single();
      if (error) {
        setShipmentModal({ open: true, loading: false, shipment: null, error: error.message });
        return;
      }
      // Aligne la détection d'annulation avec la liste (status, cancelled_at ou relation)
      const shipment = normalizeCancelled(data);
      setShipmentModal({ open: true, loading: false, shipment, error: null });
    } catch (err) {
      setShipmentModal({ open: true, loading: false, shipment: null, error: err.message });
    }
  };
  
  // Handler pour fermer la modale
  const handleCloseShipment = () => {
    setShipmentModal(prev => ({ ...prev, open: false }));
  };
  
  // Fonction pour obtenir une URL signée pour un fichier de stockage Supabase
  const getSignedUrl = async (bucket, filename) => {
    try {
      const { data, error } = await supabase
        .storage
        .from(bucket)
        .createSignedUrl(filename, 300); // URL valide pendant 5 minutes

      if (error) throw error;
      return data.signedUrl;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'URL signée pour ${filename}:`, error);
      throw error;
    }
  };

  // Fonction pour afficher un document dans une modale
  const handleViewDocument = async (shipment, documentType) => {
    try {
      let url, title, filename;
      
      // Récupérer l'ID de la transaction de paiement associée à cette expédition
      const { data: paymentData, error: paymentError } = await supabase
        .from('payments')
        .select('transaction_id')
        .eq('id', shipment.payment_id)
        .single();
      
      if (paymentError) throw paymentError;
      
      if (documentType === 'receipt') {
        filename = `${paymentData.transaction_id}.pdf`;
        url = await getSignedUrl('receipt', filename);
        title = 'Reçu de paiement';
      } else if (documentType === 'shipping') {
        filename = `Formulaire_${paymentData.transaction_id}.pdf`;
        url = await getSignedUrl('formulaires', filename);
        title = 'Formulaire d&apos;expédition';
      }

      setDocumentModal({ open: true, title, url });
    } catch (error) {
      toast.error(`Erreur lors de l'affichage du document. Le fichier n'existe peut-être pas.`);
    }
  };

  // Fonction pour télécharger un document
  const handleDownloadDocument = async (shipment, documentType) => {
    try {
      let url, filename, downloadName;
      
      // Récupérer l'ID de la transaction de paiement associée à cette expédition
      const { data: paymentData, error: paymentError } = await supabase
        .from('payments')
        .select('transaction_id')
        .eq('id', shipment.payment_id)
        .single();
      
      if (paymentError) throw paymentError;
      
      if (documentType === 'receipt') {
        filename = `${paymentData.transaction_id}.pdf`;
        url = await getSignedUrl('receipt', filename);
        downloadName = `Reçu_${paymentData.transaction_id}.pdf`;
      } else if (documentType === 'shipping') {
        filename = `Formulaire_${paymentData.transaction_id}.pdf`;
        url = await getSignedUrl('formulaires', filename);
        downloadName = filename;
      }

      // Télécharger le fichier
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Créer un lien temporaire pour le téléchargement
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toast.error(`Erreur lors du téléchargement du document. Le fichier n'existe peut-être pas.`);
    }
  };
  
  // Fonction pour fermer la modale de document
  const closeDocumentModal = () => {
    try {
      // Révoquer l'URL si c'est un Blob local
      if (documentModal?.url && documentModal.url.startsWith('blob:')) {
        URL.revokeObjectURL(documentModal.url);
      }
    } catch (_) {
      // ignoré intentionnellement
    }
    setDocumentModal({ open: false, title: '', url: '' });
  };

  // Fonction pour gérer le tracking d'une expédition
  const handleTrackShipment = async (trackingNumber) => {
    if (!trackingNumber || trackingNumber.startsWith('CD')) {
      return;
    }

    setTrackingModal({ open: true, loading: true, trackingNumber, trackingInfo: null, error: null });

    try {
      const response = await trackShipments([trackingNumber]);
      setTrackingModal(prev => ({ ...prev, loading: false, trackingInfo: response }));
    } catch (err) {
      const errorMessage = err.response?.data?.details?.errors?.[0]?.message || err.response?.data?.message || 'Erreur lors du suivi de l\'expédition. Veuillez vérifier le numéro et réessayer.';
      setTrackingModal(prev => ({ ...prev, loading: false, error: errorMessage }));
    }
  };

  // Fonction pour fermer la modale de tracking
  const closeTrackingModal = () => {
    setTrackingModal({ open: false, loading: false, trackingNumber: null, trackingInfo: null, error: null });
  };

  /**
   * Ouvre la modale de confirmation d'annulation pour l'expédition donnée
   * - Cette action est réservée aux envois FedEx (tracking ne commence pas par "CD")
   */
  const openCancelModal = (shipment) => {
    setCancelModal({ open: true, loading: false, shipment, error: null });
  };

  /** Ferme la modale d'annulation */
  const closeCancelModal = () => {
    setCancelModal({ open: false, loading: false, shipment: null, error: null });
  };

  /**
   * Confirme l'annulation auprès de notre backend (qui appelle FedEx)
   * - Met à jour l'état local à 'cancelled' en cas de succès
   */
  const confirmCancelShipment = async () => {
    if (!cancelModal.shipment?.tracking_number) return;
    setCancelModal((s) => ({ ...s, loading: true, error: null }));
    try {
      const tn = cancelModal.shipment.tracking_number;
      const resp = await cancelFedexShipment(tn);
      // Mise à jour locale: utiliser l'enregistrement renvoyé si disponible, sinon fallback
      const nowIso = new Date().toISOString();
      setShipments((prev) => prev.map((s) => {
        if (s.tracking_number !== tn) return s;
        if (resp?.shipment) return { ...s, ...resp.shipment, cancelled: true };
        return { ...s, status: 'cancelled', cancelled_at: nowIso, updated_at: nowIso, cancelled: true };
      }));
      toast.success("Expédition annulée avec succès.");
      closeCancelModal();
    } catch (err) {
      const errorMessage = err.response?.data?.details?.errors?.[0]?.message || err.response?.data?.message || err.message || "Échec de l'annulation de l'expédition.";
      setCancelModal((s) => ({ ...s, loading: false, error: errorMessage }));
      toast.error(errorMessage);
    }
  };

  // Libellés des statuts pour affichage dans le select et la modale
  const statusLabels = {
    pending: 'En attente',
    created: 'Expédition créée',
    in_progress: 'En cours',
    delivered: 'Livré',
    returned: 'Retourné'
  };

  /**
   * Ouvre la modale de confirmation de changement de statut
   * @param {object} shipment - enregistrement courant
   * @param {string} newStatus - nouveau statut choisi
   */
  const openStatusModal = (shipment, newStatus) => {
    setStatusModal({ open: true, loading: false, shipment, newStatus, error: null });
  };

  /** Ferme la modale de statut */
  const closeStatusModal = () => setStatusModal({ open: false, loading: false, shipment: null, newStatus: null, error: null });

  /**
   * Confirme et applique le changement de statut sur la table shipments
   */
  const confirmStatusChange = async () => {
    if (!statusModal.shipment || !statusModal.newStatus) return;
    setStatusModal((s) => ({ ...s, loading: true, error: null }));
    try {
      const { id } = statusModal.shipment;
      const next = statusModal.newStatus;
      const nowIso = new Date().toISOString();
      const { error } = await supabase
        .from('shipments')
        .update({ status: next, updated_at: nowIso })
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      // Mettre à jour la liste locale
      setShipments((prev) => prev.map((s) => (s.id === id ? { ...s, status: next, updated_at: nowIso } : s)));
      closeStatusModal();
    } catch (err) {
      setStatusModal((s) => ({ ...s, loading: false, error: err.message || 'Erreur lors de la mise à jour du statut.' }));
    }
  };
  
  // Calcul pour la pagination
  const totalPages = Math.ceil(shipments.length / perPage);
  const paginatedShipments = shipments.slice((page - 1) * perPage, page * perPage);
  // Génère la liste des pages avec ellipses
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    if (page <= 4) {
      pages.push(1, 2, 3, 4, 5, '...', totalPages);
      return pages;
    }
    if (page >= totalPages - 3) {
      pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      return pages;
    }
    pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
    return pages;
  };
  
  const handleSearchChange = (e) => {
    setSearchParams({ ...searchParams, [e.target.name]: e.target.value });
  };
  
  const handleResetSearch = () => {
    // Réinitialiser tous les paramètres de recherche
    setSearchParams({ searchTerm: '', searchStatus: '', startDate: '', endDate: '', searchScope: 'all' });
    
    // Recharger les données avec la requête initiale
    setLoading(true);
    setError(null);
    
    // Utiliser directement la requête Supabase au lieu d'appeler fetchShipments
    supabase
      .from('shipments')
      .select(`*, payment:payments(*, user:users(*)), cancelled_shipments(*)`)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) {
          setError(error.message);
        } else {
          const list = (data || []).map((s) => {
            const cs = Array.isArray(s.cancelled_shipments) ? s.cancelled_shipments : [];
            const cancelled = s.status === 'cancelled' || !!s.cancelled_at || cs.length > 0;
            return { ...s, cancelled };
          });
          setShipments(list);
        }
        setLoading(false);
        setPage(1);
      });
  };
  
  /**
   * Recherche d'expéditions dans la base de données Supabase
   * 
   * Cette fonction optimisée interroge directement la base de données Supabase pour trouver 
   * des expéditions correspondant aux critères de recherche. Elle utilise une approche en deux étapes :
   * 1. Recherche directe dans la table shipments (recipient_name, recipient_phone, tracking_number)
   * 2. Si aucun résultat n'est trouvé, recherche dans les tables liées (users via payments)
   * 
   * Optimisations apportées :
   * - Utilisation d'une seule requête RPC pour la recherche complète quand possible
   * - Meilleure gestion des cas limites et des valeurs nulles
   * - Optimisation des jointures et des filtres
   * - Débogage amélioré avec des logs conditionnels
   * 
   * @param {Event} e - Événement de soumission du formulaire
   */
  /**
   * Gère la soumission du formulaire de recherche.
   * Utilise le nettoyage des caractères et le scoring harmonisés avec UsersList.js
   * @param {Event} e - Événement de soumission du formulaire
   */
  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    // Débogage conditionnel
    const DEBUG = false; // Mettre à true pour activer les logs de débogage
    const logDebug = (message, data) => {
      if (DEBUG) {
        console.log(`[Recherche] ${message}`, data || '');
      }
    };
    
    try {
      // Préparation et nettoyage des paramètres de recherche
      const searchTerm = searchParams.searchTerm?.trim() || '';
      const startDate = searchParams.startDate || null;
      const endDate = searchParams.endDate || null;
      const searchStatus = searchParams.searchStatus || '';
      
      logDebug('Paramètres de recherche:', { searchTerm, startDate, endDate });
      
      // Stratégie 1: Recherche directe dans la table shipments (toujours exécutée)
      let shipmentsData = [];

      // Construction de la requête de base avec jointures
      let query = supabase
        .from('shipments')
        .select(`*, payment:payments(*, user:users(*)), cancelled_shipments(*)`)
        .order('created_at', { ascending: false })
        .limit(5000); // Limite très élevée pour garantir un ensemble complet

      // Pré-filtre texte côté DB (EXACTEMENT comme UsersList):
      // - on découpe en tokens
      // - on construit une unique clause OR avec ilike pour chaque champ x token
      if (searchTerm && searchTerm.trim()) {
        // NOTE: La fonction RPC search_shipments_with_family contient une erreur de syntaxe SQL
        // et ne peut pas être utilisée pour le moment. Nous utilisons donc la méthode standard.
        // Une fois la fonction SQL corrigée, nous pourrons réactiver ce code.

        // Champs concernés par la recherche côté serveur (JSONB exclus)
        const fieldsForDb = ['recipient_name', 'recipient_address', 'recipient_phone', 'tracking_number', 'carrier', 'status'];
        // Une seule clause OR agrégeant chaque combinaison champ x token
        const orParts = [];

        // Préparation des tokens pour la recherche
        const cleanedSearch = cleanText(searchTerm.toLowerCase().trim());
        const tokensRaw = cleanedSearch.split(/\s+/).filter(Boolean);
        const tokens = tokensRaw.map(removeAccents);

        for (const t of tokens) {
          for (const f of fieldsForDb) {
            // Pour tous les champs texte, on utilise ilike pour la simplicité
            orParts.push(`${f}.ilike.%${t}%`);
          }
        }
        if (orParts.length > 0) {
          // PostgREST n'accepte qu'une seule clause OR -> on la construit en CSV
          query = query.or(orParts.join(','));
          logDebug('Clause OR (DB):', orParts.slice(0, 10));
        }
      }

      // Filtre d'étendue (scope) : FedEx => tracking_number ne commence pas par CD
      if ((searchParams.searchScope || 'all') === 'fedex') {
        query = query.not('tracking_number', 'ilike', 'CD%');
      }

      // Filtre statut
      if (searchStatus) {
        query = query.eq('status', searchStatus);
        logDebug('Filtre statut', searchStatus);
      }

      // Ajout des filtres de date
      if (startDate) {
        query = query.gte('created_at', `${startDate} 00:00:00`);
        logDebug(`Filtre date de début (00:00): ${startDate}`);
      }
      if (endDate) {
        query = query.lt('created_at', `${nextDayYMD(endDate)} 00:00:00`);
        logDebug(`Filtre date de fin (< lendemain 00:00): ${endDate}`);
      }

      // Exécution de la requête principale (même sans aucun filtre)
      const { data: queryData, error: queryError } = await query;
      if (queryError) throw queryError;
      shipmentsData = (queryData || []).map(normalizeCancelled);
      logDebug(`Résultats de la recherche (DB): ${shipmentsData.length}`);

      // Requête complémentaire: inclure les dossiers avec des membres de famille
      // pour couvrir les cas où seul family_members matche le terme
      // Stratégie: même jeux de filtres (date/statut/scope), mais sans OR texte,
      // et ciblage des enregistrements avec family_members non nul
      if (searchTerm && searchTerm.trim()) {
        let famQuery = supabase
          .from('shipments')
          .select(`*, payment:payments(*, user:users(*)), cancelled_shipments(*)`)
          .not('family_members', 'is', null)
          .order('created_at', { ascending: false })
          .limit(5000);

        // Scope FedEx
        if ((searchParams.searchScope || 'all') === 'fedex') {
          famQuery = famQuery.not('tracking_number', 'ilike', 'CD%');
        }
        // Statut
        if (searchStatus) {
          famQuery = famQuery.eq('status', searchStatus);
        }
        // Dates
        if (startDate) {
          famQuery = famQuery.gte('created_at', `${startDate} 00:00:00`);
        }
        if (endDate) {
          famQuery = famQuery.lt('created_at', `${nextDayYMD(endDate)} 00:00:00`);
        }

        const { data: famData, error: famError } = await famQuery;
        if (famError) throw famError;
        const famResults = (famData || []).map(normalizeCancelled);
        logDebug(`Résultats complémentaires (family_members non nul): ${famResults.length}`);

        // Fusion + déduplication par id
        if (famResults.length > 0) {
          const seenIds = new Set(shipmentsData.map(s => s.id));
          for (const s of famResults) {
            if (!seenIds.has(s.id)) {
              shipmentsData.push(s);
              seenIds.add(s.id);
            }
          }
          logDebug(`Après fusion (classique + famille): ${shipmentsData.length}`);
        }
      }
      
      // Stratégie 2: Si aucun résultat direct et qu'un terme de recherche est spécifié,
      // rechercher dans les tables liées (users via payments)
      if (searchTerm && shipmentsData.length === 0) {
        logDebug('Aucun résultat direct, recherche dans les utilisateurs');
        
        // Recherche optimisée des utilisateurs et de leurs expéditions associées
        // en utilisant une seule requête avec jointures profondes
        logDebug('Requête utilisateur pour le terme:', searchTerm);
        
        // Préparation du terme de recherche avec nettoyage supplémentaire
        const cleanSearchTerm = cleanText(searchTerm.toLowerCase().trim());
        const tokensRaw = cleanSearchTerm.split(/\s+/).filter(Boolean);
        const tokens = tokensRaw.map(removeAccents);
        
        // Utilisation d'une syntaxe plus robuste pour la recherche
        // Méthode 1: Utiliser des requêtes séparées et combiner les résultats
        const searchInField = async (field) => {
          // Essayer la recherche avec chaque token
          let combined = [];
          for (const token of tokens) {
            if (!token) continue;
            const { data: fieldData, error: fieldError } = await supabase
              .from('users')
              .select('id, firstname, lastname, email, phone')
              .ilike(field, `%${token}%`);

            if (fieldError) {
              logDebug(`Erreur lors de la recherche dans ${field} (token=${token}):`, fieldError);
              continue;
            }
            combined = combined.concat(fieldData || []);
          }

          // Dédupliquer par id
          const seen = new Set();
          const unique = combined.filter(u => {
            if (seen.has(u.id)) return false;
            seen.add(u.id);
            return true;
          });

          logDebug(`Résultats pour ${field}:`, unique.length || 0);
          return unique;
        };
        
        // Recherche dans chaque champ séparément
        const firstnameResults = await searchInField('firstname');
        const lastnameResults = await searchInField('lastname');
        const emailResults = await searchInField('email');
        const phoneResults = await searchInField('phone');
        
        // Combiner et dédupliquer les résultats
        const allResults = [...firstnameResults, ...lastnameResults, ...emailResults, ...phoneResults];
        const uniqueIds = new Set();
        const usersData = allResults.filter(user => {
          if (uniqueIds.has(user.id)) return false;
          uniqueIds.add(user.id);
          return true;
        });
        
        logDebug('Résultats combinés:', usersData);
        
        logDebug(`Utilisateurs trouvés: ${usersData?.length || 0}`);
        
        // Si des utilisateurs sont trouvés, rechercher leurs expéditions
        if (usersData?.length > 0) {
          const userIds = usersData.map(user => user.id);
          
          // Recherche des paiements en une seule requête
          const { data: relatedShipments, error: relatedError } = await supabase
            .rpc('search_shipments_by_users', { user_ids: userIds })
            .select(`*, payment:payments(*, user:users(*))`);
          
          
          // Fallback si la fonction RPC n'existe pas
          if (relatedError && relatedError.message.includes('does not exist')) {
            logDebug('Fonction RPC non disponible, utilisation de requêtes en cascade');
            
            // Recherche des paiements associés aux utilisateurs
            const { data: paymentsData, error: paymentsError } = await supabase
              .from('payments')
              .select('id')
              .in('user_id', userIds);
            
            if (paymentsError) throw paymentsError;
            logDebug(`Paiements trouvés: ${paymentsData?.length || 0}`);
            
            // Si des paiements sont trouvés, rechercher les expéditions associées
            if (paymentsData?.length > 0) {
              const paymentIds = paymentsData.map(payment => payment.id);
              
              const { data: relatedShipmentsData, error: relatedShipmentsError } = await supabase
                .from('shipments')
                .select(`*, payment:payments(*, user:users(*)), cancelled_shipments(*)`)
                .in('payment_id', paymentIds)
                .order('created_at', { ascending: false });
              
              if (relatedShipmentsError) throw relatedShipmentsError;
              shipmentsData = (relatedShipmentsData || []).map(normalizeCancelled);
              logDebug(`Expéditions liées aux paiements: ${shipmentsData.length}`);
            }
          } else if (relatedError) {
            throw relatedError;
          } else {
            shipmentsData = (relatedShipments || []).map(normalizeCancelled);
            logDebug(`Expéditions liées aux utilisateurs (via RPC): ${shipmentsData.length}`);
          }
        }
      }
      
      // Si une recherche est active, on trie localement par pertinence
      // Copie exacte de la structure dans UsersList
      const activeSearch = searchTerm || '';
      if (activeSearch && activeSearch.trim() && Array.isArray(shipmentsData)) {
        // Nettoyer et normaliser les termes de recherche
        const cleanedSearch = cleanText(activeSearch.toLowerCase().trim());
        // Tokens et phrase complète normalisés (sans accents)
        const tokensRaw = cleanedSearch.split(/\s+/).filter(Boolean);
        const tokens = tokensRaw.map(removeAccents);
        const fullPhrase = removeAccents(cleanedSearch);
        
        // Filtrage côté client pour les champs JSONB comme family_members
        // qui ne peuvent pas être filtrés avec ilike côté serveur
        let filteredData = shipmentsData;
        
        // Si des tokens sont présents, on filtre d'abord sur family_members
        // avant de faire le scoring et le tri
        if (tokens.length > 0) {
          // Recherche spécifique dans family_members pour chaque expédition
          filteredData = shipmentsData.filter(shipment => {
            // Vérifier si l'expédition correspond déjà à d'autres critères
            // Si oui, on la garde sans vérifier family_members
            const haystack = buildHaystackNormalized(shipment);
            
            // Vérifier si au moins un token est présent dans le haystack
            for (const token of tokens) {
              if (haystack.includes(token)) {
                return true;
              }
            }
            
            // Si aucun token n'est trouvé, l'expédition ne correspond pas
            return false;
          });
          
          logDebug(`Filtrage côté client (incluant family_members): ${filteredData.length}/${shipmentsData.length} résultats`);
        }

        // Tri décroissant par score, puis retombée sur le tri existant
        const sorted = [...filteredData].sort((a, b) => {
          const sa = computeSearchScore(a, tokens, fullPhrase);
          const sb = computeSearchScore(b, tokens, fullPhrase);
          if (sb !== sa) return sb - sa;
          // Secondaire: date desc
          const da = a.created_at ? new Date(a.created_at).getTime() : 0;
          const db = b.created_at ? new Date(b.created_at).getTime() : 0;
          return db - da;
        });
        setShipments(sorted.map(normalizeCancelled));
      } else {
        setShipments(shipmentsData.map(normalizeCancelled));
      }
      setPage(1); // Réinitialisation de la pagination
      
      // Notification des résultats à l'utilisateur
      const resultCount = shipmentsData.length;
      if (resultCount === 0) {
        toast('Aucun résultat ne correspond à votre recherche.', {
          icon: 'ℹ️',
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          },
        });
      } else {
        toast.success(`${resultCount} résultat(s) trouvé(s)`);
      }
    } catch (error) {
      // Gestion des erreurs
      logDebug('Erreur lors de la recherche:', error);
      console.error('Erreur lors de la recherche:', error);
      setError('Erreur lors de la recherche. Veuillez réessayer.');
      toast.error('Erreur lors de la recherche. Veuillez réessayer.');
    } finally {
      // Fin du chargement dans tous les cas
      setLoading(false);
    }
  };

  return (
    <div ref={topRef}>
      <AdminLoader show={loading} message="Chargement des expéditions…" />
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold whitespace-nowrap">Gestion des expéditions</h2>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <AdminButton 
            type="secondary" 
            icon="refresh"
            className="w-full sm:w-auto justify-center"
            onClick={() => {
              setLoading(true);
              const fetchShipments = async () => {
                try {
                  const { data, error } = await supabase
                    .from('shipments')
                    .select(`*, payment:payments(*, user:users(*)), cancelled_shipments(*)`)
                    .order('created_at', { ascending: false });
                  if (error) throw error;
                  
                  setShipments((data || []).map(normalizeCancelled));
                } catch (err) {
                  setError(err.message);
                } finally {
                  setLoading(false);
                }
              };
              fetchShipments();
            }}
          >
            Actualiser
          </AdminButton>
          <AdminButton 
            type="primary" 
// ...
            to="/admin/shipments/new" 
            icon="add"
            className="w-full sm:w-auto justify-center"
          >
            Nouvelle expédition
          </AdminButton>
        </div>
      </div>
      
      {/* Statistiques synthétiques */}
      <div className="bg-white p-3 rounded-lg shadow mb-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full md:w-auto">
            {/* Total demandes */}
            <div className="rounded-lg p-3 bg-gradient-to-r from-orange-50 to-white border border-orange-200 shadow-sm">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#e88c00]/15 text-[#e88c00]">
                  {/* icon clipboard/list */}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M9 2a1 1 0 00-1 1H6a2 2 0 00-2 2v13a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2h-2a1 1 0 00-1-1H9zm0 2h6v1H9V4zm-1 5h8v2H8V9zm0 4h8v2H8v-2z"/></svg>
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-orange-700/80 font-medium">Demandes d&apos;expédition</div>
                  <div className="leading-none mt-1 text-2xl font-extrabold text-orange-900">{stats.loading ? '—' : stats.total}</div>
                </div>
              </div>
            </div>
            {/* FedEx */}
            <div className="rounded-lg p-3 bg-gradient-to-r from-emerald-50 to-white border border-emerald-200 shadow-sm">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#0b8e36]/15 text-[#0b8e36]">
                  {/* icon truck */}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M3 4h11v10H3V4zm11 0h3l4 4v6h-7V4zm-8 14a2 2 0 11-.001-3.999A2 2 0 016 18zm10 0a2 2 0 11-.001-3.999A2 2 0 0116 18z"/></svg>
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-emerald-700/80 font-medium">Expédition FedEx</div>
                  <div className="leading-none mt-1 text-2xl font-extrabold text-emerald-900">{stats.loading ? '—' : stats.fedex}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end gap-2 w-full md:w-auto">
            <div className="w-full sm:w-auto">
              <label htmlFor="statsStart" className="block text-xs font-medium text-gray-600 mb-1">Date de début</label>
              <input
                id="statsStart"
                type="date"
                className="w-full sm:w-auto px-2 py-1 border border-gray-300 rounded-md text-sm"
                value={statsDates.start}
                onChange={(e) => setStatsDates((d) => ({ ...d, start: e.target.value }))}
              />
            </div>
            <div className="w-full sm:w-auto">
              <label htmlFor="statsEnd" className="block text-xs font-medium text-gray-600 mb-1">Date de fin</label>
              <input
                id="statsEnd"
                type="date"
                className="w-full sm:w-auto px-2 py-1 border border-gray-300 rounded-md text-sm"
                value={statsDates.end}
                onChange={(e) => setStatsDates((d) => ({ ...d, end: e.target.value }))}
              />
            </div>
            <button
              type="button"
              onClick={() => fetchStats(statsDates.start || undefined, statsDates.end || undefined)}
              className="w-full sm:w-auto inline-flex justify-center items-center h-9 px-3 rounded-md text-sm font-medium text-white bg-[#0b8e36] hover:bg-green-700"
              title="Mettre à jour les statistiques"
            >
              Mettre à jour
            </button>
          </div>
        </div>
        {stats.error && (
          <div className="mt-2 text-xs text-red-600">{stats.error}</div>
        )}
      </div>

      {/* Filtres et pagination */}
      <div className="bg-white p-4 rounded-lg shadow mb-4">
        <form onSubmit={handleSearchSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-2">
              <label htmlFor="searchTerm" className="block text-sm font-medium text-gray-700 mb-1">
                Recherche
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  id="searchTerm"
                  name="searchTerm"
                  placeholder="Nom, prénom, email, membres de famille..."
                  className="flex-1 w-full md:w-96 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 text-sm"
                  value={searchParams.searchTerm || ''}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            <div>
              <label htmlFor="searchScope" className="block text-sm font-medium text-gray-700 mb-1">
                Périmètre
              </label>
              <select
                id="searchScope"
                name="searchScope"
                className="w-full max-w-[140px] px-2 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                value={searchParams.searchScope || 'all'}
                onChange={handleSearchChange}
                aria-label="Périmètre de recherche"
                title="Périmètre de recherche"
              >
                <option value="all">Tout</option>
                <option value="fedex">Expéditions FedEx</option>
              </select>
            </div>
            <div>
              <label htmlFor="searchStatus" className="block text-sm font-medium text-gray-700 mb-1">
                Statut
              </label>
              <select
                id="searchStatus"
                name="searchStatus"
                className="w-full max-w-[170px] px-2 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 relative z-0"
                value={searchParams.searchStatus || ''}
                onChange={handleSearchChange}
                aria-label="Filtrer par statut"
                title="Filtrer par statut"
              >
                <option value="">Tous les statuts</option>
                {['pending','created','in_progress','delivered','returned'].map((k) => (
                  <option key={k} value={k}>{statusLabels[k]}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                Date de début
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                className="w-full max-w-[160px] px-2 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                value={searchParams.startDate || ''}
                onChange={handleSearchChange}
              />
            </div>
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                Date de fin
              </label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                className="w-full max-w-[160px] px-2 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                value={searchParams.endDate || ''}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <label htmlFor="perPage" className="text-sm font-medium text-gray-700">Afficher:</label>
              <select
                id="perPage"
                className="border border-gray-300 rounded-md text-sm p-1"
                value={perPage}
                onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); scrollToTop(); }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={handleResetSearch}
                className="w-full sm:w-auto inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Réinitialiser
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                Rechercher
              </button>
            </div>
          </div>
        </form>
      </div>
      
      {/* Shipments Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="sm:hidden">
          {paginatedShipments.length === 0 && (
            <div className="px-6 py-4 text-center text-gray-500">Aucune expédition trouvée.</div>
          )}
          {paginatedShipments.map((shipment) => (
            <div key={shipment.id} className={`border-t border-gray-200 p-4 ${shipment.cancelled ? 'bg-red-50' : ''}`}>
              <div className="flex flex-col gap-2">
                <div className="w-full">
                  <div className="font-semibold text-gray-900 whitespace-pre-line break-words w-full flex items-center gap-2">
                    {shipment.with_family ? (
                      <UserGroupIcon className="h-5 w-5 flex-shrink-0 text-gray-600" aria-hidden="true" />
                    ) : (
                      <UserIcon className="h-5 w-5 flex-shrink-0 text-gray-600" aria-hidden="true" />
                    )}
                    <span className="min-w-0 break-words">{shipment.recipient_name}</span>
                  </div>
                  <div className="text-xs text-gray-600 mt-1 pl-7">
                    Date de naissance : {formatBirthDate(shipment.payment?.user?.birth_date)}
                  </div>
                  {shipment.with_family && Array.isArray(shipment.family_members) && shipment.family_members.length > 0 && (
                    <div className="mt-1 pl-7 space-y-1">
                      {shipment.family_members.map((member, index) => (
                        <div key={member.id || index} className="text-xs text-blue-600 break-words">
                          {member.firstname} {member.lastname}
                          <span className="text-gray-500"> — {formatBirthDate(member.birth_date)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-1 whitespace-pre-line break-words w-full">{shipment.recipient_address}</div>
                </div>

                <div className="w-full flex justify-end">
                  {shipment.cancelled ? (
                    <span className="text-red-700 font-semibold text-sm">Expédition annulée</span>
                  ) : (
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(shipment.status)}`}>
                      {getStatusLabel(shipment.status)}
                    </span>
                  )}
                </div>

                <div className="mt-1 grid grid-cols-1 gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500">Transporteur</span>
                    <span className="text-sm text-gray-900 font-medium">{shipment.carrier || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500">Dates</span>
                    <span className="text-xs text-gray-700 text-right">
                      <span className="block">Maj: {formatDate(shipment.updated_at)}</span>
                      <span className="block">Créée: {formatDate(shipment.created_at)}</span>
                    </span>
                  </div>
                </div>

                <div className="mt-2 flex flex-col gap-2">
                  <button
                    onClick={() => handleShowShipment(shipment.id)}
                    className="w-full px-3 py-2 bg-[#0b8e36] text-white rounded hover:bg-green-700 whitespace-nowrap flex items-center justify-center gap-2"
                    title="Voir les détails de l'expédition"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Détails
                  </button>
                  <button
                    onClick={() => handleEdit(shipment)}
                    disabled={!!shipment.cancelled}
                    aria-disabled={!!shipment.cancelled}
                    className={`w-full px-3 py-2 rounded whitespace-nowrap flex items-center justify-center gap-2 text-white ${shipment.cancelled ? 'bg-gray-400 cursor-not-allowed opacity-60' : 'bg-[#e88c00] hover:bg-orange-700'}`}
                    title="Modifier l'expédition"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Modifier
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="hidden sm:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-1/4 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Destinataire
              </th>
              <th className="w-1/4 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Document
              </th>
              <th className="w-1/6 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Transporteur
              </th>
              <th className="w-1/6 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="w-1/6 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="w-1/6 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedShipments.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="px-6 py-4 text-center text-gray-500">Aucune expédition trouvée.</td>
              </tr>
            )}
            {paginatedShipments.map((shipment) => (
              <tr key={shipment.id} className={shipment.cancelled ? 'bg-red-100' : ''}>
                {columns.map((column, index) => (
                  <td 
                  key={index} 
                  className={`px-4 py-4 text-sm text-gray-900 ${
                    column.key === 'recipient_name' ? 'w-1/4 break-words' : 
                    column.key === 'documents_to_be_sent' ? 'w-1/4 break-words' :
                    column.key === 'carrier' ? 'w-1/6 break-words' :
                    column.key === 'status' ? 'w-1/6' :
                    column.key === 'created_at' ? 'w-1/6' : ''
                  }`}
                >
                    {column.type === 'status' ? (
                      shipment.cancelled ? (
                        <span className="text-red-700 font-semibold">Expédition annulée</span>
                      ) : (
                        <div className="relative inline-block min-w-[160px] md:min-w-[180px]">
                          <select
                            className={`appearance-none border rounded-full text-[11px] font-semibold leading-5 focus:outline-none focus:ring-orange-500 focus:border-orange-500 ${getStatusSelectClass(shipment[column.key])} whitespace-nowrap px-2 pr-6 py-0.5 w-full`}
                            value={shipment[column.key] || 'pending'}
                            onChange={(e) => {
                              const next = e.target.value;
                              if (next && next !== shipment[column.key]) {
                                openStatusModal(shipment, next);
                              }
                            }}
                            aria-label="Modifier le statut"
                            title="Modifier le statut"
                          >
                            {['pending','created','in_progress','delivered','returned'].map((k) => (
                              <option key={k} value={k}>{statusLabels[k]}</option>
                            ))}
                          </select>
                          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-current">
                            <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                            </svg>
                          </span>
                        </div>
                      )
                    ) : column.type === 'date' ? (
                      column.key === 'created_at'
                        ? (shipment[column.key]
                            ? (
                              <div className="flex flex-col">
                                <span className="text-xs text-gray-800">Mise à jour : {formatDate(shipment.updated_at)}</span>
                                <span className="text-xs text-gray-500">Créée : {formatDate(shipment[column.key])}</span>
                              </div>
                            ) : '—')
                        : (shipment[column.key] ? formatDate(shipment[column.key]) : '—')
                    ) : column.key === 'recipient_name' ? (
                      <div className="break-words">
                        <div className="font-medium flex items-start min-w-0">
                          {shipment.with_family ? (
                            <UserGroupIcon className="h-[20px] w-[20px] min-h-[20px] min-w-[20px] mr-1 text-gray-600" aria-hidden="true" />
                          ) : (
                            <UserIcon className="h-[20px] w-[20px] min-h-[20px] min-w-[20px] mr-1 text-gray-600" aria-hidden="true" />
                          )}
                          <span className="min-w-0 break-words">{shipment.recipient_name}</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1 pl-6">
                          Date de naissance : {formatBirthDate(shipment.payment?.user?.birth_date)}
                        </div>
                        {/* Affichage des membres de la famille */}
                        {shipment.with_family && shipment.family_members && (
                          <div className="text-xs text-blue-600 mt-1 pl-6">
                            {(() => {
                              let familyMembers = shipment.family_members;
                              if (typeof familyMembers === 'string') {
                                try {
                                  familyMembers = JSON.parse(familyMembers);
                                } catch {
                                  familyMembers = [];
                                }
                              }
                              if (Array.isArray(familyMembers) && familyMembers.length > 0) {
                                return (
                                  <div className="flex flex-col">
                                    {familyMembers.map((member, idx) => {
                                      // Gestion des différents formats possibles pour les membres de famille
                                      let memberName = '';
                                      if (typeof member === 'string') {
                                        memberName = member;
                                      } else if (member?.name || member?.fullname) {
                                        memberName = member.name || member.fullname;
                                      } else if (member?.firstname || member?.lastname) {
                                        // Format avec firstname/lastname
                                        memberName = [member.firstname, member.lastname].filter(Boolean).join(' ');
                                      }
                                      
                                      return memberName ? (
                                        <div key={idx} className="flex items-start gap-1 min-w-0">
                                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full inline-block flex-shrink-0 mt-1.5"></span>
                                          <span className="min-w-0 break-words">
                                            {memberName}
                                            {typeof member === 'object' && (
                                              <span className="text-gray-500"> — {formatBirthDate(member.birth_date)}</span>
                                            )}
                                          </span>
                                        </div>
                                      ) : null;
                                    })}
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">{shipment.recipient_address}</div>
                      </div>
                    ) : column.key === 'documents_to_be_sent' ? (
                      <div>
                        {(() => {
                          let docs = shipment.documents_to_be_sent;
                          if (typeof docs === 'string') {
                            try {
                              docs = JSON.parse(docs);
                            } catch {
                              docs = [];
                            }
                          }
                          if (Array.isArray(docs) && docs.length > 0) {
                            return (
                              <div className="flex flex-col gap-1">
                                {docs.map((doc, idx) => (
                                  <div key={doc.act_id || idx} className="flex items-center gap-1.5">
                                    <svg className="h-5 w-5 min-w-5 flex-shrink-0 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 8h8M8 12h6M8 16h8"/></svg>
                                    <span className="text-xs">{doc.act_title || (typeof doc === 'string' ? doc : JSON.stringify(doc))}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          return <span className="italic text-gray-400 text-xs">Aucun document</span>;
                        })()}
                      </div>
                    ) : column.key === 'carrier' ? (
                      <div className="flex flex-col gap-1">
                        <div>{shipment[column.key]}</div>
                        <div className="flex flex-row gap-2">
                          {shipment.label_url && (
                            <button
                              onClick={(event) => handleDownloadFedexLabel(event, shipment)}
                              className="flex items-center px-2 py-1 mt-1 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 transition-colors duration-200 justify-between whitespace-nowrap w-auto"
                              title="Télécharger l'étiquette d'expédition"
                            >
                              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ minWidth: '16px', minHeight: '16px' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 12h4M10 16h4" />
                              </svg>
                              Étiquette
                            </button>
                          )}
                          {shipment.tracking_number && !shipment.tracking_number.startsWith('CD') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTrackShipment(shipment.tracking_number);
                              }}
                              className="flex items-center px-2 py-1 mt-1 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500 transition-colors duration-200 justify-between whitespace-nowrap w-auto"
                              title="Suivre l'expédition"
                            >
                              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ minWidth: '16px', minHeight: '16px' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                              Suivre
                            </button>
                          )}
                        </div>
                      </div>
                    ) : column.key === 'created_at' ? (
                      shipment[column.key] ? (
                        <div className="flex flex-col">
                          {/* Toujours afficher la mention de mise à jour, avec fallback */}
                          <span className="text-xs text-gray-800">Mise à jour : {formatDate(shipment.updated_at)}</span>
                          {/* Date de création avec libellé explicite */}
                          <span className="text-xs text-gray-500">Créée : {formatDate(shipment[column.key])}</span>
                        </div>
                      ) : '—'
                    ) : (
                      shipment[column.key]
                    )}
                  </td>
                ))}
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex flex-col space-y-2">
                    <div className="flex flex-col space-y-2">
                      <button
                        onClick={() => handleShowShipment(shipment.id)}
                        className="px-3 py-1 bg-[#0b8e36] text-white rounded hover:bg-green-700 whitespace-nowrap flex items-center gap-1.5 inline-flex"
                        title="Voir les détails de l'expédition"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Détails
                      </button>
                      <button
                        onClick={() => handleEdit(shipment)}
                        disabled={!!shipment.cancelled}
                        aria-disabled={!!shipment.cancelled}
                        className={`px-3 py-1 rounded whitespace-nowrap flex items-center gap-1.5 inline-flex text-white ${shipment.cancelled ? 'bg-gray-400 cursor-not-allowed opacity-60' : 'bg-[#e88c00] hover:bg-orange-700'}`}
                        title="Modifier l'expédition"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Modifier
                      </button>
                    </div>
                    {/* Bouton Preuve de livraison: visible uniquement si livré et tracking FedEx */}
                    {shipment.status === 'delivered' && shipment.tracking_number && !shipment.tracking_number.startsWith('CD') && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewProofOfDelivery(shipment);
                        }}
                        className={`px-3 py-1 rounded whitespace-nowrap flex items-center gap-1.5 inline-flex text-white ${podLoadingTN === shipment.tracking_number ? 'bg-gray-500 cursor-wait' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                        title="Preuve de livraison"
                        disabled={podLoadingTN === shipment.tracking_number}
                        aria-busy={podLoadingTN === shipment.tracking_number}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m-9 8h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v11a2 2 0 002 2z" />
                        </svg>
                        {podLoadingTN === shipment.tracking_number ? 'Chargement…' : 'Preuve de livraison'}
                      </button>
                    )}
                    {shipment.tracking_number && !shipment.tracking_number.startsWith('CD') && shipment.status !== 'in_progress' && shipment.status !== 'delivered' && shipment.status !== 'cancelled' && !shipment.cancelled && (
                      <button
                        onClick={() => openCancelModal(shipment)}
                        className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 whitespace-nowrap flex items-center gap-1.5 inline-flex"
                        title="Annuler l'expédition"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Annuler
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      
      {/* Pagination améliorée */}
      {totalPages > 1 && (
        <div className="flex flex-col items-center justify-center gap-3 mt-8 mb-12">
          {/* Info plage affichée */}
          <div className="text-xs text-gray-600 text-center">
            Affichage <span className="font-semibold">{(page - 1) * perPage + 1}</span>
            –<span className="font-semibold">{Math.min(page * perPage, shipments.length)}</span>
            {' '}sur <span className="font-semibold">{shipments.length}</span>
          </div>
          {/* Contrôles */}
          <div className="flex flex-wrap items-center justify-center gap-1">
            <button
              className="px-2 py-1.5 rounded-md border text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => goToPage(1)}
              disabled={page === 1}
              aria-label="Première page"
              title="Première page"
            >
              <span className="hidden sm:inline">«</span>
              <span className="inline sm:hidden">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M11.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L7.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  <path fillRule="evenodd" d="M16.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L12.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
              </span>
            </button>
            <button
              className="px-2 py-1.5 rounded-md border text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => goToPage(page - 1)}
              disabled={page === 1}
              aria-label="Page précédente"
              title="Page précédente"
            >
              <span className="hidden sm:inline">‹</span>
              <span className="inline sm:hidden">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L8.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
              </span>
            </button>
            {getPageNumbers().map((p, idx) => (
              typeof p === 'string' ? (
                <span key={`ellipsis-${idx}`} className="px-2 py-1.5 text-xs text-gray-500 select-none">…</span>
              ) : (
                <button
                  key={p}
                  className={
                    `px-3 py-1.5 rounded-md border text-xs font-semibold ` +
                    (p === page
                      ? 'bg-[#e88c00] text-white border-[#e88c00] cursor-default'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300')
                  }
                  onClick={() => goToPage(p)}
                  aria-current={p === page ? 'page' : undefined}
                  title={`Aller à la page ${p}`}
                >
                  {p}
                </button>
              )
            ))}
            <button
              className="px-2 py-1.5 rounded-md border text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => goToPage(page + 1)}
              disabled={page === totalPages}
              aria-label="Page suivante"
              title="Page suivante"
            >
              <span className="hidden sm:inline">›</span>
              <span className="inline sm:hidden">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 11-1.414-1.414L11.586 10 7.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
            </button>
            <button
              className="px-2 py-1.5 rounded-md border text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => goToPage(totalPages)}
              disabled={page === totalPages}
              aria-label="Dernière page"
              title="Dernière page"
            >
              <span className="hidden sm:inline">»</span>
              <span className="inline sm:hidden">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 11-1.414-1.414L12.586 10 8.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  <path fillRule="evenodd" d="M3.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 11-1.414-1.414L7.586 10 3.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
            </button>
          </div>
        </div>
      )}
      
      {/* Modale d'informations d'expédition */}
      <Transition show={shipmentModal.open} as={React.Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={handleCloseShipment}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            {/* Centrer le modal */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-5xl overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="bg-gradient-to-r from-[#0b8e36] to-[#e88c00] p-4 text-white">
                  <div className="flex justify-between items-center">
                    <Dialog.Title
                      as="h3"
                      className="text-xl font-semibold leading-6 text-white flex items-center gap-2"
                    >
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2v-2" /></svg>
                      Détails de l&apos;expédition
                    </Dialog.Title>
                    <button
                      type="button"
                      className="text-white hover:text-gray-200 transition-colors"
                      onClick={handleCloseShipment}
                    >
                      <span className="sr-only">Fermer</span>
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="space-y-6">
                    {shipmentModal.loading ? (
                      <div className="flex justify-center items-center h-40">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-[#e88c00]"></div>
                      </div>
                    ) : shipmentModal.error ? (
                      <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-red-600 text-center font-medium">{shipmentModal.error}</div>
                    ) : shipmentModal.shipment ? (
                      <div>
                      {shipmentModal.shipment.cancelled && (
                        <div className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 flex items-center gap-2">
                          {/* Icône d'alerte */}
                          <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                          </svg>
                          <span className="font-bold">Expédition annulée</span>
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Colonne Statut & Tracking */}
                        <div className="bg-gray-50 p-5 rounded-lg shadow-sm border border-gray-100 md:col-start-1 md:row-start-1 md:order-1">
                          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                            <svg className="h-6 w-6 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                            <span className="font-bold text-lg text-[#0b8e36]">Statut de l&apos;expédition</span>
                          </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" /></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Numéro de suivi</div>
                              <div className="font-semibold">
                                {shipmentModal.shipment.tracking_number && shipmentModal.shipment.tracking_number.startsWith('CD')
                                  ? 'En attente'
                                  : shipmentModal.shipment.tracking_number || 'Non spécifié'}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zm10 0a2 2 0 11-4 0 2 2 0 014 0z" /><path d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10" /><path d="M1 8h4" /><path d="M16 8h4M16 16h2a2 2 0 002-2v-3a4 4 0 00-4-4h-2" /></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Transporteur</div>
                              <div className="font-semibold">{shipmentModal.shipment.carrier}</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Statut</div>
                              <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(shipmentModal.shipment.status)}`}>
                                {getStatusLabel(shipmentModal.shipment.status)}
                              </span>
                            </div>
                          </div>
                          
                          {shipmentModal.shipment.delivery_date && (
                            <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                              <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                                <svg className="h-5 w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 font-medium">Date de livraison</div>
                                <div className="font-semibold">{format(new Date(shipmentModal.shipment.delivery_date), 'dd/MM/yyyy')}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Colonne Destinataire */}
                      <div className="bg-gray-50 p-5 rounded-lg shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                          <svg className="h-6 w-6 min-w-6 flex-shrink-0 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          <span className="font-bold text-lg text-[#e88c00]">Destinataire</span>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#e88c00]/10 p-2 rounded-full">
                              {shipmentModal.shipment.with_family ? (
                                <svg className="h-5 w-5 min-w-5 flex-shrink-0 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                              ) : (
                                <svg className="h-5 w-5 min-w-5 flex-shrink-0 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M6 20v-2a6 6 0 0112 0v2"/></svg>
                              )}
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Nom</div>
                              <div className="font-semibold">{shipmentModal.shipment.recipient_name}</div>
                              <div className="text-xs text-gray-500">
                                Date de naissance : {formatBirthDate(shipmentModal.shipment.payment?.user?.birth_date)}
                              </div>
                            </div>
                          </div>
                          
                          {shipmentModal.shipment.with_family && shipmentModal.shipment.family_members && (
                            <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                              <div className="bg-[#e88c00]/10 p-2 rounded-full">
                                <svg className="h-5 w-5 min-w-5 flex-shrink-0 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                              </div>
                              <div className="w-full">
                                <div className="text-xs text-gray-500 font-medium">Membres de la famille</div>
                                <div className="font-medium">
                                  {(Array.isArray(shipmentModal.shipment.family_members) 
                                    ? shipmentModal.shipment.family_members 
                                    : (typeof shipmentModal.shipment.family_members === 'string' 
                                      ? JSON.parse(shipmentModal.shipment.family_members || '[]')
                                      : []
                                    )
                                  ).map((member, index) => (
                                    <div key={member.id || index} className="py-1 border-b border-gray-100 last:border-0">
                                      {member.firstname} {member.lastname}
                                      <span className="text-xs text-gray-500"> — {formatBirthDate(member.birth_date)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#e88c00]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-w-5 flex-shrink-0 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 21s-6-5.686-6-10a6 6 0 1112 0c0 4.314-6 10-6 10z"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Adresse</div>
                              <div className="font-semibold">{shipmentModal.shipment.recipient_address}</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#e88c00]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-w-5 flex-shrink-0 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92V19a2 2 0 01-2.18 2A19.72 19.72 0 013 5.18 2 2 0 015 3h2.09a2 2 0 012 1.72c.13.81.36 1.6.68 2.36a2 2 0 01-.45 2.11L8.09 10.91a16 16 0 006 6l1.72-1.72a2 2 0 012.11-.45c.76.32 1.55.55 2.36.68a2 2 0 011.72 2V19z"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Téléphone</div>
                              <div className="font-semibold">{shipmentModal.shipment.recipient_phone || '—'}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Colonne Documents */}
                      <div className="bg-gray-50 p-5 rounded-lg shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                          <svg className="h-6 w-6 min-w-6 flex-shrink-0 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 8h8M8 12h6M8 16h8"/></svg>
                          <span className="font-bold text-lg text-[#e88c00]">Documents à expédier</span>
                        </div>
                        
                        {(() => {
                          let docs = shipmentModal.shipment.documents_to_be_sent;
                          if (typeof docs === 'string') {
                            try {
                              docs = JSON.parse(docs);
                            } catch {
                              docs = [];
                            }
                          }
                          if (Array.isArray(docs) && docs.length > 0) {
                            return (
                              <div className="space-y-2">
                                {docs.map((doc, idx) => (
                                  <div key={doc.act_id || idx} className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                                    <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                                      <svg className="h-5 w-5 min-w-5 flex-shrink-0 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 8h8M8 12h6M8 16h8"/></svg>
                                    </div>
                                    <div className="font-medium">{doc.act_title || (typeof doc === 'string' ? doc : JSON.stringify(doc))}</div>
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          return (
                            <div className="flex items-center justify-center h-24 bg-white rounded-md shadow-sm">
                              <span className="text-gray-400 italic">Aucun document</span>
                            </div>
                          );
                        })()}
                        
                        {shipmentModal.shipment.return_status && (
                          <div className="mt-4 p-3 bg-red-50 rounded-md border border-red-100 flex items-center gap-3">
                            <div className="bg-red-100 p-2 rounded-full">
                              <svg className="h-5 w-5 min-w-5 flex-shrink-0 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 14l-4-4 4-4"/><path d="M5 10h11a4 4 0 110 8h-1"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-red-500 font-medium">Statut de retour</div>
                              <div className="font-semibold text-red-600">Retourné {shipmentModal.shipment.return_reason && <span>({shipmentModal.shipment.return_reason})</span>}</div>
                            </div>
                          </div>
                        )}
                        
                        {/* Documents à télécharger */}
                        <div className="mt-6 space-y-4">
                          {/* Reçu de paiement */}
                          <div className="border rounded-lg p-3 bg-white shadow-sm">
                            <h4 className="font-medium text-base mb-2 text-[#0b8e36]">Reçu de paiement</h4>
                            <div className="flex justify-between items-center">
                              <button
                                onClick={() => handleViewDocument(shipmentModal.shipment, 'receipt')}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                Voir
                              </button>
                              <button
                                onClick={() => handleDownloadDocument(shipmentModal.shipment, 'receipt')}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0b8e36] text-white rounded hover:bg-[#0b8e36]/90 transition-colors text-xs"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Télécharger
                              </button>
                            </div>
                          </div>
                          
                          {/* Formulaire d'expédition */}
                          <div className="border rounded-lg p-3 bg-white shadow-sm">
                            <h4 className="font-medium text-base mb-2 text-[#e88c00]">Formulaire d&apos;expédition</h4>
                            <div className="flex flex-col items-start">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleViewDocument(shipmentModal.shipment, 'shipping')}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  Voir
                                </button>
                                <button
                                  onClick={() => handleDownloadDocument(shipmentModal.shipment, 'shipping')}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#e88c00] text-white rounded hover:bg-[#e88c00]/90 transition-colors text-xs"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  Télécharger
                                </button>
                              </div>
                              <button
                                onClick={() => handleRefreshShippingForm(shipmentModal.shipment.id)}
                                className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-gray-700 text-white rounded hover:bg-gray-800 transition-colors text-xs disabled:opacity-60 disabled:cursor-not-allowed"
                                disabled={isRefreshingForm}
                                title="Régénérer le formulaire avec les dernières données"
                              >
                                {isRefreshingForm ? (
                                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                  </svg>
                                ) : (
                                  <ArrowPathIcon className="h-5 w-5 min-w-5 flex-shrink-0" aria-hidden="true" />
                                )}
                                <span>{isRefreshingForm ? 'Actualisation…' : 'Actualiser'}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
              ) : null}
                </div>
              </div>
            </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
      
      {/* Modale de confirmation de changement de statut */}
      <Transition show={statusModal.open} as={React.Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={statusModal.loading ? () => {} : closeStatusModal}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            {/* Centrage */}
            <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>

            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="flex justify-between items-start">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Confirmer le changement de statut
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={statusModal.loading ? () => {} : closeStatusModal}
                    aria-label="Fermer"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mt-3">
                  <p className="text-sm text-gray-600">
                    Voulez-vous vraiment changer le statut de cette expédition
                    {statusModal.shipment ? ` (${statusModal.shipment.tracking_number || statusModal.shipment.id})` : ''}
                    {' '}vers <span className="font-semibold">{statusLabels[statusModal.newStatus] || statusModal.newStatus}</span> ?
                  </p>
                  {statusModal.error && (
                    <div className="mt-3 bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">{statusModal.error}</div>
                  )}
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded border border-gray-300 hover:bg-gray-200 disabled:opacity-50"
                    onClick={closeStatusModal}
                    disabled={statusModal.loading}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="px-3 py-2 text-sm text-white bg-[#0b8e36] rounded hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-2"
                    onClick={confirmStatusChange}
                    disabled={statusModal.loading}
                  >
                    {statusModal.loading && (
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                      </svg>
                    )}
                    Confirmer
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
      
      {/* Modal pour le tracking */}
      <Transition show={trackingModal.open} as={React.Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={closeTrackingModal}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            {/* Centrer le modal */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-5xl overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="bg-gradient-to-r from-[#0b8e36] to-[#e88c00] p-4 text-white">
                  <div className="flex justify-between items-center">
                    <Dialog.Title
                      as="h3"
                      className="text-xl font-semibold leading-6 text-white flex items-center gap-2"
                    >
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 17l6-6-6-6" /></svg>
                      Détails du suivi
                    </Dialog.Title>
                    <button
                      type="button"
                      className="text-white hover:text-gray-200 transition-colors"
                      onClick={closeTrackingModal}
                    >
                      <span className="sr-only">Fermer</span>
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="p-6">
                  {trackingModal.loading ? (
                    <div className="flex justify-center items-center h-64">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
                    </div>
                  ) : trackingModal.error ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                      <strong className="font-bold">Erreur :</strong>
                      <span className="block sm:inline"> {trackingModal.error}</span>
                    </div>
                  ) : trackingModal.trackingInfo ? (
                    <TrackingResult trackingInfo={trackingModal.trackingInfo} />
                  ) : null}
                </div>
                
                <div className="bg-gray-50 p-4 flex justify-end border-t border-gray-100">
                  <button
                    type="button"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-[#0b8e36] border border-transparent rounded-md hover:bg-[#097a2e] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0b8e36] transition-colors"
                    onClick={closeTrackingModal}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Modale de confirmation d'annulation FedEx */}
      <Transition show={cancelModal.open} as={React.Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={cancelModal.loading ? () => {} : closeCancelModal}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>

            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="flex items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M12 18a6 6 0 110-12 6 6 0 010 12z" />
                    </svg>
                  </div>
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                    Confirmer l&apos;annulation de l&apos;expédition
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Cette action va demander l&apos;annulation auprès de FedEx pour le numéro de suivi
                      <span className="font-semibold"> {cancelModal.shipment?.tracking_number}</span>.
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      L&apos;opération est irréversible si elle aboutit. Confirmez-vous ?
                    </p>
                    {cancelModal.error && (
                      <div className="mt-3 bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded">
                        {cancelModal.error}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="button"
                    onClick={confirmCancelShipment}
                    disabled={cancelModal.loading}
                    className={`inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white ${cancelModal.loading ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-500 sm:ml-3 sm:w-auto`}
                  >
                    {cancelModal.loading ? 'Annulation…' : "Confirmer l'annulation"}
                  </button>
                  <button
                    type="button"
                    onClick={closeCancelModal}
                    disabled={cancelModal.loading}
                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-300 sm:mt-0 sm:w-auto"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Modal pour afficher les PDF */}
      <Transition show={documentModal.open} as={React.Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={closeDocumentModal}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            {/* Centrer le modal */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-5xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {documentModal.title}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={closeDocumentModal}
                  >
                    <span className="sr-only">Fermer</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="mt-2 bg-gray-100 rounded-lg p-1 h-[70vh]">
                  <iframe
                    src={documentModal.url}
                    className="w-full h-full rounded"
                    title={documentModal.title}
                  />
                </div>
                
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                    onClick={closeDocumentModal}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default ShipmentsList;
